import type { ComponentType, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { cn } from "../../utils/common/cn";
import type { SparkPoint } from "../../pages/PortfolioPerformance/lib/api/shared";

export type StatStatus = "green" | "amber" | "red";

const STATUS_STYLES: Record<StatStatus, string> = {
  green: "border-approve/40 bg-approve/10 text-approve",
  amber: "border-amber/40 bg-amber-dim text-amber",
  red: "border-decline/40 bg-decline/10 text-decline",
};

export const STATUS_LABELS: Record<StatStatus, string> = {
  green: "Healthy",
  amber: "Watch",
  red: "Attention",
};

interface StatCardProps {
  label: string;
  value?: string;
  icon: ComponentType<{ className?: string }>;
  sparkline?: SparkPoint[];
  sparkColor?: string;
  /** `comparison` says what the change is measured against ("vs Sep'26"); it is shown beside the change. */
  trend?: { value: string; direction: "up" | "down" | "flat"; goodDirection?: "up" | "down"; comparison?: string | null };
  status?: StatStatus;
  hint?: ReactNode;
  /** Put the hint on the change's own line, after it ("▼ 0.4pt vs Feb'26  of owned NI, click for detail"), instead of on a line of its own
   *  below. Without a change to sit beside, it is a line of its own as usual. */
  hintInline?: boolean;
  /** When set, the whole card becomes a link with a hover state. */
  link?: LinkProps;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  sparkline,
  sparkColor = "var(--amber)",
  trend,
  status,
  hint,
  hintInline = false,
  link,
}: StatCardProps) {
  const trendGood =
    trend && trend.direction !== "flat"
      ? trend.direction === (trend.goodDirection ?? "up")
      : undefined;

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <Icon className="size-4 shrink-0 text-primary" />
      </div>

      <div className="mt-4 flex items-end justify-between gap-4">
        {status ? (
          <span
            className={cn(
              "border px-2.5 py-1 text-xs font-semibold uppercase tracking-wider",
              STATUS_STYLES[status],
            )}
          >
            {STATUS_LABELS[status]}
          </span>
        ) : (
          <span className="text-2xl font-semibold tabular-nums text-foreground">{value}</span>
        )}

        {sparkline ? (
          <div className="h-10 w-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkline} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={sparkColor}
                  strokeWidth={1.5}
                  fill={sparkColor}
                  fillOpacity={0.15}
                  isAnimationActive={false}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </div>

      {trend ? (
        <p
          className={cn(
            "mt-3 text-xs font-medium tabular-nums",
            trendGood === undefined
              ? "text-muted-foreground"
              : trendGood
                ? "text-approve"
                : "text-decline",
          )}
        >
          {trend.direction === "up" ? "▲" : trend.direction === "down" ? "▼" : "■"} {trend.value}
          {trend.comparison ? <span className="ml-1 font-normal text-muted-foreground">{trend.comparison}</span> : null}
          {hintInline && hint ? <span className="ml-2 font-normal text-muted-foreground">{hint}</span> : null}
        </p>
      ) : null}

      {hint && (!hintInline || !trend) ? <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{hint}</p> : null}
    </>
  );

  const base = "flex flex-col border border-border bg-card p-5";

  if (link) {
    return (
      <Link
        {...link}
        className={cn(
          base,
          "text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          link.className,
        )}
      >
        {body}
      </Link>
    );
  }

  return <div className={base}>{body}</div>;
}
